import { filter_data, get_overall_average } from "reports/Tag/calculations";

// Utility function to parse JSON responses from GPT
function parseGPTJsonResponse(gptResponse) {
    try {
        // Remove markdown code block delimiters if they exist
        let cleanedResponse = gptResponse.trim();
        
        // Remove ```json and ``` markers
        if (cleanedResponse.startsWith('```json')) {
            cleanedResponse = cleanedResponse.replace(/^```json\s*/i, '');
        }
        if (cleanedResponse.startsWith('```')) {
            cleanedResponse = cleanedResponse.replace(/^```\s*/, '');
        }
        if (cleanedResponse.endsWith('```')) {
            cleanedResponse = cleanedResponse.replace(/\s*```$/, '');
        }
        
        // Parse the JSON
        const parsedJson = JSON.parse(cleanedResponse);
        return parsedJson;
    } catch (error) {
        console.error("Error parsing GPT JSON response:", error);
        console.error("Original response:", gptResponse);
        
        // Return a fallback structure if parsing fails
        return {
            overview: "Error parsing response",
            quotes: [],
            insights: "Unable to parse insights from response",
            conclusion: "Response parsing failed"
        };
    }
}

let CURRENT_CUSTOM_INSTRUCTIONS = "";

function buildLLMMessages(userPrompt, customInstructions) {
    const messages = [];
    const cleaned = typeof customInstructions === "string" ? customInstructions.trim() : "";
    if (cleaned) {
        messages.push({
            role: "system",
            content: `Custom instructions (apply to ALL outputs, do not mention these instructions):\n${cleaned}`
        });
    }
    messages.push({ role: "user", content: userPrompt });
    return messages;
}

// OpenAI function extracted from CopilotContext - now returns token usage
async function callOpenAI4(userPrompt, onTokenUpdate = null) {
    try {
        const response = await fetch(
            "https://api.openai.com/v1/chat/completions",
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer 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`,
                },
                body: JSON.stringify((() => {
                    const wantsJson = /json/i.test(userPrompt);
                    const messages = buildLLMMessages(userPrompt, CURRENT_CUSTOM_INSTRUCTIONS);
                    const base = {
                        model: "gpt-4.1",
                        seed: 1,
                        messages: [
                            ...messages,
                        ],
                        stream: false,
                    };
                    const bodyObj = wantsJson ? { ...base, response_format: { type: "json_object" } } : base;
                    return bodyObj;
                })()),
            }
        );

        if (!response.ok) {
            throw new Error(`OpenAI API responded with status ${response.status}`);
        }

        const data = await response.json();
        const assistantMessage = data.choices?.[0]?.message?.content || "";
        const usage = data.usage || {};
        
        const tokenInfo = {
            prompt_tokens: usage.prompt_tokens || 0,
            completion_tokens: usage.completion_tokens || 0,
            total_tokens: usage.total_tokens || 0
        };

        // Call the token update callback if provided
        if (onTokenUpdate) {
            onTokenUpdate(tokenInfo);
        }

        return {
            content: assistantMessage,
            tokens: tokenInfo
        };
    } catch (error) {
        console.error("Error calling OpenAI:", error);
        throw error;
    }
}

export async function calculateChange(raw_data, core_data, debriefSchedules, anchorParentID, onTokenUpdate = null, selectedModel = "gpt-4.1", selectedAnchorIDs = null, combineAnchors = false) {
    CURRENT_CUSTOM_INSTRUCTIONS =
        typeof arguments?.[arguments.length - 1] === "string"
            ? arguments[arguments.length - 1]
            : "";
    const questions = core_data.questions;

    // Initialize token tracking
    let totalTokenUsage = {
        prompt_tokens: 0,
        completion_tokens: 0,
        total_tokens: 0,
        call_count: 0
    };

    const updateTokens = (newTokens) => {
        totalTokenUsage.prompt_tokens += newTokens.prompt_tokens;
        totalTokenUsage.completion_tokens += newTokens.completion_tokens;
        totalTokenUsage.total_tokens += newTokens.total_tokens;
        totalTokenUsage.call_count += 1;
        
        // Notify the UI of token updates
        if (onTokenUpdate) {
            onTokenUpdate(totalTokenUsage);
        }
    };

    // Find the selected anchor parent by ID
    const selectedAnchorParent = core_data.categories[0].categories.find(cat => cat.id === anchorParentID);
    
    if (!selectedAnchorParent) {
        throw new Error(`Anchor parent with ID ${anchorParentID} not found`);
    }

    // Calculate overall scores across all data
    const overallLatestScore = get_overall_average(raw_data[0].responses);
    const overallLastScore = get_overall_average(raw_data[0].last);
    const overallChange = overallLatestScore - overallLastScore;

    // Get overall factor changes across all data
    const latestFactorScores = calculateAverageScoresPerFactor(raw_data[0].responses);
    const lastFactorScores = calculateAverageScoresPerFactor(raw_data[0].last);
    const overallFactorChanges = getChangeData(latestFactorScores, lastFactorScores, questions, raw_data[0].responses, raw_data[0].last);

    // Sort and get top positive and negative changes
    const sortedByChange = overallFactorChanges.sort((a,b) => b.score_change - a.score_change);
    const positiveChanges = sortedByChange.filter(f => f.score_change > 0).slice(0, 3);
    const negativeChanges = sortedByChange.filter(f => f.score_change < 0).slice(-3).reverse();

    // Generate GPT summaries for top changes
    const topPositiveChanges = [];
    for (const change of positiveChanges) {
        if (change.score_change > 0) {
            const feedbackSummaryPrompt = `
            Please analyze the following feedback data for a factor that has improved:
            
            **Factor:** ${change.factor_name}
            **Score Change:** +${change.score_change.toFixed(2)}
            
            **Latest Feedback:**
            ${change.latest_feedback || "No feedback provided"}
            
            **Previous Feedback:**
            ${change.last_feedback || "No feedback provided"}
            
            Provide a 1-sentence summary explaining the key reason for this positive change based on the feedback comparison. Focus on what specifically improved.
            
            Return only the 1-sentence summary, no additional formatting.
            `;
            
            const gptResult = await callOpenAI4(feedbackSummaryPrompt, updateTokens);
            
            topPositiveChanges.push({
                factor_name: change.factor_name,
                score_change: change.score_change,
                latest_score: change.latest_score,
                last_score: change.last_score,
                feedback_summary: gptResult.content
            });
        }
    }

    const topNegativeChanges = [];
    for (const change of negativeChanges) {
        if (change.score_change < 0) {
            const feedbackSummaryPrompt = `
            Please analyze the following feedback data for a factor that has declined:
            
            **Factor:** ${change.factor_name}
            **Score Change:** ${change.score_change.toFixed(2)}
            
            **Latest Feedback:**
            ${change.latest_feedback || "No feedback provided"}
            
            **Previous Feedback:**
            ${change.last_feedback || "No feedback provided"}
            
            Provide a 1-sentence summary explaining the key reason for this negative change based on the feedback comparison. Focus on what specifically declined or became problematic.
            
            Return only the 1-sentence summary, no additional formatting.
            `;
            
            const gptResult = await callOpenAI4(feedbackSummaryPrompt, updateTokens);
            
            topNegativeChanges.push({
                factor_name: change.factor_name,
                score_change: change.score_change,
                latest_score: change.latest_score,
                last_score: change.last_score,
                feedback_summary: gptResult.content
            });
        }
    }

    // Collect anchor-specific data
    const anchorDataResults = [];
    // Determine which anchors to process (all by default, or filtered by selection)
    const optionsToProcess = Array.isArray(selectedAnchorIDs) && selectedAnchorIDs.length > 0
        ? selectedAnchorParent.options.filter(opt => selectedAnchorIDs.includes(opt.id))
        : selectedAnchorParent.options;

    // If combining anchors, compute one combined dataset and return a single entry
    if (combineAnchors && optionsToProcess.length > 1) {
        const filterList = optionsToProcess.map(opt => ({ id: opt.id }));
        const filteredData = filter_data(raw_data[0].responses, filterList);
        const lastFilteredData = filter_data(raw_data[0].last, filterList);

        const latestFactorScores = calculateAverageScoresPerFactor(filteredData);
        const lastFactorScores = calculateAverageScoresPerFactor(lastFilteredData);
        const overallAverage = get_overall_average(filteredData);
        const lastOverallAverage = get_overall_average(lastFilteredData);
        const changeDataWithFeedback = getChangeData(latestFactorScores, lastFactorScores, questions, filteredData, lastFilteredData).sort((a,b)=>a.score_change-b.score_change);

        const prompt = `
        Please analyze the following data for combined anchors: ${optionsToProcess.map(o=>o.name).join(", ")}
        ---
        **Scores**  
        - Latest: ${Math.round(overallAverage * 10 * 10) / 10}  
        - Previous: ${Math.round(lastOverallAverage * 10 * 10) / 10}

        **Factor Changes**
        ${changeDataWithFeedback.map(f=>`- ${f.factor_name}: ${f.score_change} - \n\n Most recent Feedback ${f.latest_feedback} \n\n Previous Feedback ${f.last_feedback}`).join("\n")}
        ---
        
        Provide JSON formatted response with the following structure:
        1. Overview of the changes in 3-4 sentences. Focus on explaining what the feedback indicates about this combined group, and the key themes and trends.
        2. 3 Quotes from Feedback that illustrate the biggest shifts or themes. Just include the quote text only in a list.
        3. Deeper Insights & Possible Root Causes of the changes in scores (2-3 sentences).
        4. Positive Trends - 2-3 sentences on strategies or policies that impacted things positively.
        5. Conclusion - 1-2 sentences wrapping up key takeaways and any final thoughts.
        
          **Tone & Format**  
          - Keep it clear, concise, and professional.  
          - Never use markdown syntax for anything, just plain text.
          - Return STRICTLY VALID JSON (RFC 8259). Output JSON ONLY with no explanation before or after.
          - All keys and string values MUST use double quotes.

        **JSON Format**
        {
            "overview": "",
            "quotes": [],
            "insights": "",
            "positive_trends": "",
            "conclusion": ""
        }
        `

        const gptResult = await callOpenAI4(prompt, updateTokens);
        const parsedGptAnalysis = parseGPTJsonResponse(gptResult.content);

        anchorDataResults.push({
            anchor_id: optionsToProcess.map(o=>o.id),
            anchor_name: optionsToProcess.map(o=>o.name).join(", "),
            overall_average: overallAverage,
            last_overall_average: lastOverallAverage,
            change_data: changeDataWithFeedback.map(item => ({
                factor_name: item.factor_name,
                latest_score: item.latest_score,
                last_score: item.last_score,
                score_change: item.score_change
            })),
            gpt_analysis: parsedGptAnalysis
        });
    } else {
        // Iterate through each of the selected anchor options in the selected anchor parent
        for (const option of optionsToProcess) {
            const anchorID = option.id;
            const anchorName = option.name;
            const filteredData = filter_data(raw_data[0].responses, [{id:anchorID}]);
            const lastFilteredData = filter_data(raw_data[0].last, [{id:anchorID}]);
        
        // Get the anchor data
        const latestFactorScores = calculateAverageScoresPerFactor(filteredData);
        const lastFactorScores = calculateAverageScoresPerFactor(lastFilteredData);
        
        const overallAverage = get_overall_average(filteredData);
        const lastOverallAverage = get_overall_average(lastFilteredData);

        const changeDataWithFeedback = getChangeData(latestFactorScores, lastFactorScores, questions, filteredData, lastFilteredData).sort((a,b)=>a.score_change-b.score_change);

        const prompt = `
        Please analyze the following data for organization name:${anchorName}
        ---
        **Scores**  
        - Latest: ${Math.round(overallAverage * 10 * 10) / 10}  
        - Previous: ${Math.round(lastOverallAverage * 10 * 10) / 10}

        **Factor Changes**
        ${changeDataWithFeedback.map(f=>`- ${f.factor_name}: ${f.score_change} - \n\n Most recent Feedback ${f.latest_feedback} \n\n Previous Feedback ${f.last_feedback}`).join("\n")}
        ---
        
        Provide JSON formatted response with the following structure:
        1. Overview of the changes in 3-4 sentences. Focus on explaining what the feedback indicates about this organization, and the key themes and trends. Be sharp and concise. You're writing this for an executive, they want the highlights.
        2. 3 Quotes from Feedback that illustrate the biggest shifts or themes. Just include the quote text only in a list. Remove any named entities (Cliq, or others)
        3. Deeper Insights & Possible Root Causes of the changes in scores (2-3 sentences). Focus only on explaining the biggest shifts, and do not explain the scores.
        4. Positive Trends - 2-3 sentences on strategies or policies that impacted things positively. Explain causation based on feedback.
        5. Conclusion - 1-2 sentences wrapping up key takeaways and any final thoughts.
    
          **Tone & Format**  
          - Keep it clear, concise, and professional.  
          - Use **bold text** and headings for clarity.  
          - Aim for an **engaging, insight-focused** style that helps readers quickly grasp the main points.
          - Even though we are anchoring the analysis in complex culture factors, we want to communicate things in a simple way without the jargon.
          - Never use markdown syntax for anything, just plain text.
          - Return STRICTLY VALID JSON (RFC 8259). Output JSON ONLY with no explanation before or after.
          - All keys and string values MUST use double quotes. Arrays must contain JSON strings only.
          - Escape any internal double quotes in values as \\" or replace them with single quotes.
          - Do NOT include trailing commas, control characters, or line breaks inside values.

        **JSON Format**
        {
            "overview": "",
            "quotes": [],
            "insights": "",
            "positive_trends": "",
            "conclusion": ""
        }
        `

        const gptResult = await callOpenAI4(prompt, updateTokens);
        const parsedGptAnalysis = parseGPTJsonResponse(gptResult.content);

        const changeDataClean = changeDataWithFeedback.map(item => ({
            factor_name: item.factor_name,
            latest_score: item.latest_score,
            last_score: item.last_score,
            score_change: item.score_change
        }));

        const outputData = {
            anchor_id: anchorID,
            anchor_name: option.name,
            overall_average: overallAverage,
            last_overall_average: lastOverallAverage,
            change_data: changeDataClean,
            gpt_analysis: parsedGptAnalysis
        }
        
            anchorDataResults.push(outputData);
        }
    }

    // Calculate final costs
    const inputCost = (totalTokenUsage.prompt_tokens / 1000000) * 2;
    const outputCost = (totalTokenUsage.completion_tokens / 1000000) * 8;
    const totalCost = inputCost + outputCost;

    // Return structured data as requested
    return {
        overall_summary: {
            overall_average: overallLatestScore,
            overall_change: overallChange,
            previous_average: overallLastScore
        },
        top_positive_changes: topPositiveChanges,
        top_negative_changes: topNegativeChanges,
        anchor_data: anchorDataResults,
        selected_anchor_parent: {
            id: selectedAnchorParent.id,
            name: selectedAnchorParent.name
        },
        token_usage: {
            ...totalTokenUsage,
            input_cost: inputCost,
            output_cost: outputCost,
            total_cost: totalCost
        }
    };
}




/**
 * Calculate the average scores per culture factor.
 * Each entry in data is expected to have a “questions” array.
 * We use a composite key "factor_dimension" (e.g. "0_1") to track each factor.
 */
function calculateAverageScoresPerFactor(data) {
    const factorScores = {};
    data.forEach(entry => {
      const questions = entry.questions || [];
      questions.forEach(question => {
        const factor = question.factor;
        const dimension = question.id; // assuming "id" represents the dimension id
        const response = question.response;
        if (factor != null && dimension != null && response != null) {
          const key = `${factor}_${dimension}`;
          if (factorScores[key]) {
            factorScores[key].sum += response;
            factorScores[key].count += 1;
          } else {
            factorScores[key] = { sum: response, count: 1, factor, dimension };
          }
        }
      });
    });
    // Calculate averages for each factor key
    const averages = {};
    Object.keys(factorScores).forEach(key => {
      averages[key] = factorScores[key].sum / factorScores[key].count;
    });
    return averages;
  }

  function filterFeedback(responses, factor, dimension) {
    if (!responses || !Array.isArray(responses)) return [];
    
    const feedbackList = [];
    responses.forEach(entry => {
      const questions = entry.feedback || [];
      questions.forEach(question => {
        if (question.factor === factor && question.id === dimension && question.feedback) {
          feedbackList.push({
            feedback: question.feedback,
            question: question.question || "",
            follow_up: question.follow_up || "",
            follow_up_question: question.follow_up_question || "",
            prompt_question: question.prompt_question || ""
          });
        }
      });
    });
    
    return feedbackList;
  }

  function getFactorFeedback(factorTuple, responses, options = {}) {
    console.log(factorTuple, responses)
    if (!factorTuple) return "";
    const [factor, dimension] = factorTuple;
    
    // Filter the responses
    const feedbackList = filterFeedback(responses, factor, dimension);
    if (!feedbackList.length) return "";
    
    // Depending on options, choose a formatting strategy. For now, we use one format.
    return formatFeedback(feedbackList, options);
  }
  
  function formatFeedback(feedbackList, options = {}) {
    if (!feedbackList.length) return "";
    
    const { headerName } = options;
    let formatted = headerName
      ? `### Feedback from ${headerName} responses:\n\n`
      : "### Feedback from responses:\n\n";
    
    feedbackList.forEach(item => {
      // If prompt_question exists, handle it differently
      if (item.prompt_question) {
        formatted += "Follow up: " + item.prompt_question + "\n";
        formatted += "Response: " + (item.feedback || "") + "\n";
        formatted += "\n";
        return;
      }
      
      if (item.feedback) {
        formatted += "Question asked: " + (item.question || "") + "\n";
        formatted += "Response: " + item.feedback + "\n";
      }
      
      if (item.follow_up && item.follow_up_question) {
        formatted += "Follow up: " + item.follow_up_question + "\n";
        formatted += "Response: " + item.follow_up + "\n";
      }
      
      formatted += "\n";
    });
    
    formatted += "### END OF FEEDBACK \n\n";
    return formatted;
  }

  function getFactorName(questionsStructure, factorTuple) {
    if (!factorTuple) return null;
    const [factorId, dimensionId] = factorTuple;
    const dimensions = questionsStructure.dimensions || [];
    // Find the dimension whose id equals dimensionId+1 (adjust if necessary)
    const dimension = dimensions.find(dim => dim.id === dimensionId + 1);
    if (!dimension) return null;
    const factor = dimension.factors.find(fact => fact.id === factorId + 1);
    if (!factor) return null;
    return factor.title;
  }

  function getChangeData(latestFactorScores, lastFactorScores, questions, filteredData = null, lastFilteredData = null){

    // Get the union of all factor keys (composite keys: "factor_dimension")
    const allFactorKeys = new Set([
        ...Object.keys(latestFactorScores),
        ...Object.keys(lastFactorScores)
      ]);

      const factorChanges = [];

       // Process each culture factor sequentially
       for (let key of allFactorKeys) {
        const parts = key.split("_");
        const factorId = parseInt(parts[0], 10);
        const dimensionId = parseInt(parts[1], 10);
    
        const factorName = getFactorName(questions, [factorId, dimensionId]);
        const latestScore =
          latestFactorScores[key] !== undefined
            ? latestFactorScores[key]
            : "No data";
        const lastScore =
          lastFactorScores[key] !== undefined ? lastFactorScores[key] : "No data";

        // Track the numeric score change
        const scoreChange =
          typeof latestScore === "number" && typeof lastScore === "number"
            ? latestScore - lastScore
            : 0;

        const latestFeedback = filteredData ? getFactorFeedback(
            [factorId, dimensionId],
            filteredData
          ) : "";
          const lastFeedback = lastFilteredData ? getFactorFeedback(
            [factorId, dimensionId],
            lastFilteredData
          ) : "";

        factorChanges.push({
          factor_id: key,
          factor_name: factorName,
          latest_score: latestScore,
          last_score: lastScore,
          score_change: scoreChange,
          latest_feedback: latestFeedback,
          last_feedback: lastFeedback
        });
    }

    return factorChanges;
  }